import type { SearchSuggestion } from '../view-models/search-suggestion';
import { isCategorySuggestion } from './type-guards';

/**
 * Determines the search category based on the current page pathname.
 *
 * @param pathname - The current page pathname (e.g., '/projects', '/professionals')
 * @returns The category string ('projects', 'professionals', 'products', 'articles') or empty string
 *
 * @example
 * ```typescript
 * const category = getCurrentPageCategory('/professionals/john-doe');
 * // Returns: 'professionals'
 * ```
 */
export function getCurrentPageCategory(pathname: string): string {
    if (pathname.startsWith('/projects')) return 'projects';
    if (pathname.startsWith('/professionals')) return 'professionals';
    if (pathname.startsWith('/products')) return 'products';
    if (pathname.startsWith('/articles')) return 'articles';
    return '';
}

/**
 * Builds a search URL with category context based on the current page pathname.
 *
 * @param searchTerm - The search term to include in the URL
 * @param pathname - Optional pathname to determine category context. If not provided, uses window.location.pathname
 * @returns The complete search URL with query parameters
 *
 * @example
 * ```typescript
 * // Use current page pathname
 * const url = buildSearchUrl('modern kitchen');
 * // Returns: '/search?search=modern+kitchen&category=products' (if on /products page)
 *
 * // Use explicit pathname
 * const url = buildSearchUrl('modern kitchen', '/professionals');
 * // Returns: '/search?search=modern+kitchen&category=professionals'
 * ```
 */
export function buildSearchUrl(searchTerm: string, pathname?: string): string {
    const currentPathname =
        pathname ??
        (typeof window !== 'undefined' ? window.location.pathname : '');

    const category = getCurrentPageCategory(currentPathname);
    const searchParams = new URLSearchParams({ search: searchTerm });
    if (category) {
        searchParams.set('category', category);
    }
    return `/search?${searchParams.toString()}`;
}

/**
 * Configuration options for handling search suggestion clicks
 */
export interface HandleSearchSuggestionClickOptions {
    /**
     * Function to navigate to a URL
     */
    onNavigate: (url: string) => void;
    /**
     * Function to add a search term to recent searches
     */
    addRecentSearch: (search: string) => void;
    /**
     * Optional tracking configuration for analytics
     */
    trackingConfig?: {
        onSearchSuggestionClick?: (query: string) => void;
        onSuggestedCategoryClick?: (title: string) => void;
    };
}

/**
 * Handles search suggestion clicks.
 * This handler works with both string suggestions (keywords) and SearchSuggestion objects
 * (professionals, categories).
 *
 * @param suggestion - The suggestion that was clicked (string or SearchSuggestion object)
 * @param options - Configuration options including navigation and tracking
 *
 * @example
 * ```typescript
 * handleSearchSuggestionClick(suggestion, {
 *     onNavigate: (url) => navigate(url),
 *     addRecentSearch: (search) => saveToLocalStorage(search),
 *     trackingConfig: {
 *         onSearchSuggestionClick: (query) => trackEvent('search', query),
 *     }
 * });
 * ```
 */
export function handleSearchSuggestionClick(
    suggestion: SearchSuggestion | string,
    options: HandleSearchSuggestionClickOptions,
) {
    const { onNavigate, addRecentSearch, trackingConfig } = options;

    const redirectUrl = (url: string) => {
        // Note: may not work on localhost:3000, works after build
        onNavigate(url);
    };

    const hasTitleLink =
        typeof suggestion === 'object' &&
        'title' in suggestion &&
        'link' in suggestion;

    if (typeof suggestion === 'string') {
        trackingConfig?.onSearchSuggestionClick?.(suggestion);

        const url = buildSearchUrl(suggestion);
        addRecentSearch(suggestion);
        redirectUrl(url);
    } else if (hasTitleLink) {
        // Track category-specific event
        if (isCategorySuggestion(suggestion)) {
            trackingConfig?.onSuggestedCategoryClick?.(suggestion.title);
        }
        addRecentSearch(suggestion.title);
        redirectUrl(suggestion.link);
    }
    // No fallback: object suggestions must always have title+link.
    // Never pass the raw SearchSuggestion object to buildSearchUrl or addRecentSearch.
}
